// ============================================================
// src/lib/fitParser.js
// Parse file .fit dan .gpx di browser (tanpa server)
// Rollaco Cycling Academy — Tahap 5
// ============================================================

// ── .GPX Parser (pure JS, no library needed) ─────────────────
// GPX adalah XML biasa — kita parse manual
export function parseGpx(fileContent) {
  const parser = new DOMParser()
  const xml    = parser.parseFromString(fileContent, 'application/xml')

  // Cek error parse
  const parseError = xml.querySelector('parsererror')
  if (parseError) throw new Error('File GPX tidak valid atau rusak.')

  // Ambil semua trackpoint
  const trkpts = Array.from(xml.querySelectorAll('trkpt'))
  if (trkpts.length === 0) throw new Error('Tidak ada data trackpoint dalam file GPX.')

  // ── Hitung jarak total (Haversine formula) ────────────────
  let totalDistanceM = 0
  for (let i = 1; i < trkpts.length; i++) {
    const lat1 = parseFloat(trkpts[i-1].getAttribute('lat'))
    const lon1 = parseFloat(trkpts[i-1].getAttribute('lon'))
    const lat2 = parseFloat(trkpts[i].getAttribute('lat'))
    const lon2 = parseFloat(trkpts[i].getAttribute('lon'))
    totalDistanceM += haversine(lat1, lon1, lat2, lon2)
  }

  // ── Hitung durasi ─────────────────────────────────────────
  let durationSeconds = null
  const times = trkpts
    .map(pt => pt.querySelector('time')?.textContent)
    .filter(Boolean)
  if (times.length >= 2) {
    const start = new Date(times[0])
    const end   = new Date(times[times.length - 1])
    durationSeconds = Math.round((end - start) / 1000)
  }

  // ── Ambil HR dari extensions ──────────────────────────────
  const hrValues = trkpts
    .map(pt => {
      const hr = pt.querySelector('hr, heartrate, HeartRateBpm value')
      return hr ? parseInt(hr.textContent) : null
    })
    .filter(v => v && v > 0 && v < 250)

  const avgHr = hrValues.length > 0
    ? Math.round(hrValues.reduce((a, b) => a + b, 0) / hrValues.length)
    : null

  // ── Ambil Cadence ─────────────────────────────────────────
  const cadValues = trkpts
    .map(pt => {
      const cad = pt.querySelector('cad, cadence, Cadence')
      return cad ? parseInt(cad.textContent) : null
    })
    .filter(v => v && v > 0 && v < 300)

  const avgCadence = cadValues.length > 0
    ? Math.round(cadValues.reduce((a, b) => a + b, 0) / cadValues.length)
    : null

  // ── Ambil Power ───────────────────────────────────────────
  const pwrValues = trkpts
    .map(pt => {
      const pwr = pt.querySelector('power, Watts, watts')
      return pwr ? parseInt(pwr.textContent) : null
    })
    .filter(v => v && v > 0 && v < 3000)

  const avgPower = pwrValues.length > 0
    ? Math.round(pwrValues.reduce((a, b) => a + b, 0) / pwrValues.length)
    : null

  // ── Hitung avg speed ──────────────────────────────────────
  const distanceKm = totalDistanceM / 1000
  const durationMin = durationSeconds ? Math.round(durationSeconds / 60) : null
  const avgSpeedKmh = (durationSeconds && distanceKm > 0)
    ? parseFloat((distanceKm / (durationSeconds / 3600)).toFixed(1))
    : null

  // ── Ambil nama aktivitas ──────────────────────────────────
  const trackName = xml.querySelector('trk > name')?.textContent
    || xml.querySelector('name')?.textContent
    || null

  return {
    distance:    distanceKm > 0 ? parseFloat(distanceKm.toFixed(2)) : null,
    duration:    durationMin,
    avg_speed:   avgSpeedKmh,
    heart_rate:  avgHr,
    cadence:     avgCadence,
    power_watts: avgPower,
    notes:       trackName,
    source:      'file_gpx',
  }
}

// ── .FIT Parser (pakai library fit-file-parser via CDN) ───────
// Library: https://github.com/jimmykane/fit-parser
// Di-load sebagai UMD global di index.html atau di sini via dynamic import
export function parseFit(arrayBuffer) {
  return new Promise((resolve, reject) => {
    // Cek apakah library sudah tersedia (di-load via script tag di index.html)
    if (typeof window.fitparser === 'undefined' && typeof window.FitParser === 'undefined') {
      reject(new Error(
        'Library fit-file-parser belum tersedia. ' +
        'Tambahkan script tag di public/index.html — lihat instruksi di bawah.'
      ))
      return
    }

    // Support dua nama global yang umum dipakai library ini
    const FitParser = window.FitParser || window.fitparser?.FitParser

    try {
      const fitParser = new FitParser({
        force: true,
        speedUnit: 'km/h',
        lengthUnit: 'km',
        temperatureUnit: 'celsius',
        elapsedRecordField: true,
        mode: 'cascade',
      })

      fitParser.parse(arrayBuffer, (error, data) => {
        if (error) {
          reject(new Error('Gagal parse file .fit: ' + error))
          return
        }

        try {
          const result = extractFitData(data)
          resolve(result)
        } catch (e) {
          reject(new Error('Gagal ekstrak data dari .fit: ' + e.message))
        }
      })
    } catch (e) {
      reject(new Error('Error saat parse .fit: ' + e.message))
    }
  })
}

// ── Ekstrak data dari hasil parse .fit ────────────────────────
function extractFitData(data) {
  // Data ada di data.activity.sessions[0] atau data.records
  const session = data?.activity?.sessions?.[0]
  const records = data?.activity?.sessions?.[0]?.laps?.flatMap(l => l.records || [])
    || data?.records
    || []

  // Dari session summary (lebih akurat)
  if (session) {
    return {
      distance:    session.total_distance
                     ? parseFloat(session.total_distance.toFixed(2))
                     : null,
      duration:    session.total_elapsed_time
                     ? Math.round(session.total_elapsed_time / 60)
                     : null,
      avg_speed:   session.avg_speed
                     ? parseFloat(session.avg_speed.toFixed(1))
                     : null,
      heart_rate:  session.avg_heart_rate
                     ? Math.round(session.avg_heart_rate)
                     : null,
      cadence:     session.avg_cadence
                     ? Math.round(session.avg_cadence)
                     : null,
      power_watts: session.avg_power
                     ? Math.round(session.avg_power)
                     : null,
      notes:       data?.activity?.sport || null,
      source:      'file_fit',
    }
  }

  // Fallback: hitung rata-rata dari records
  if (records.length > 0) {
    const avg = (field) => {
      const vals = records.map(r => r[field]).filter(v => v != null && v > 0)
      return vals.length > 0
        ? vals.reduce((a, b) => a + b, 0) / vals.length
        : null
    }

    const speeds  = records.map(r => r.speed).filter(v => v > 0)
    const avgSpeed = speeds.length > 0
      ? parseFloat((speeds.reduce((a,b) => a+b,0) / speeds.length).toFixed(1))
      : null

    // Hitung jarak dari records (jika ada timestamp)
    const timestamps = records.map(r => r.timestamp).filter(Boolean)
    let durationMin = null
    if (timestamps.length >= 2) {
      const diff = new Date(timestamps[timestamps.length-1]) - new Date(timestamps[0])
      durationMin = Math.round(diff / 60000)
    }

    return {
      distance:    null, // sulit hitung akurat dari records tanpa posisi GPS
      duration:    durationMin,
      avg_speed:   avgSpeed,
      heart_rate:  avg('heart_rate') ? Math.round(avg('heart_rate')) : null,
      cadence:     avg('cadence')    ? Math.round(avg('cadence'))    : null,
      power_watts: avg('power')      ? Math.round(avg('power'))      : null,
      notes:       null,
      source:      'file_fit',
    }
  }

  throw new Error('Tidak ada data sesi atau record ditemukan dalam file .fit')
}

// ── Haversine formula (jarak antara 2 koordinat GPS) ─────────
function haversine(lat1, lon1, lat2, lon2) {
  const R   = 6371000 // radius bumi dalam meter
  const dLat = toRad(lat2 - lat1)
  const dLon = toRad(lon2 - lon1)
  const a =
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
    Math.sin(dLon/2) * Math.sin(dLon/2)
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a))
}

function toRad(deg) { return deg * Math.PI / 180 }

// ── Deteksi tipe file ─────────────────────────────────────────
export function detectFileType(filename) {
  const ext = filename.toLowerCase().split('.').pop()
  if (ext === 'gpx') return 'gpx'
  if (ext === 'fit') return 'fit'
  return null
}

// ── Helper: baca file sebagai text (untuk GPX) ───────────────
export function readFileAsText(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload  = e => resolve(e.target.result)
    reader.onerror = () => reject(new Error('Gagal membaca file'))
    reader.readAsText(file)
  })
}

// ── Helper: baca file sebagai ArrayBuffer (untuk FIT) ────────
export function readFileAsArrayBuffer(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload  = e => resolve(e.target.result)
    reader.onerror = () => reject(new Error('Gagal membaca file'))
    reader.readAsArrayBuffer(file)
  })
}
